/* eslint-disable react-refresh/only-export-components */
import { createContext, ReactNode, useCallback, useContext } from 'react';
import { GridApi } from 'ag-grid-community';

import { useInstance } from 'app/libs/use-instance';

// Q: Почему ипсользуется GridApiContainer вместо того,
// чтобы использовать useState напрямую и положить туда AgGridApi?
//
// A: React Context будет вызывать перерисовку дочерних компонентов (в т.ч. AgGrid компонента),
// после того, как сама таблица будет инициализированна и отправит AgGridApi в Context.
// GridApiContainer остается неизменным после создания Context/Provider и не изменяется при перерисовке
// родительских компонентов.
// GridApiContainer не является реактивным объектом. После того, как в GridApiContainer
// будет добавлен AgGridApi, перерисовка компонентов не будет вызвана.
// Соответственно, не стоит использовать функцию, возращенную из useDataGridApi при первом рендере компонента,
// т.к. вызов этой функции вернет null.
// Использовать функцию, которую вернул useDataGridApi, можно в обработчиках событий (например, по клику на кнопке),
// когда AgGrid таблица была проинициализированна и объект AgGridApi был добавлен в GridApiContainer
// через Context с помощью хука useDataGridApiSetter.

class GridApiContainer {
  private resolver: (api: GridApi) => void = () => {};

  public api: Promise<GridApi>;

  constructor() {
    this.api = new Promise((resolve) => {
      this.resolver = resolve;
    });
  }

  setGridApi(gridApi: GridApi) {
    this.resolver(gridApi);
  }
}

const context = createContext<GridApiContainer>(new GridApiContainer());

const Provider = context.Provider;
// eslint-disable-next-line mobx/missing-observer
export const DataGridApiProvider = ({
  children,
}: {
  children: ReactNode | ReactNode[];
}) => {
  const container = useInstance(() => new GridApiContainer());

  return <Provider value={container}>{children}</Provider>;
};

export const useDataGridApi = () => {
  const container = useContext(context);

  const getApi = useCallback(async () => await container.api, [container]);

  return getApi;
};

export const useDataGridApiSetter = () => {
  const container = useContext(context);
  const setter = useCallback(
    (api: GridApi) => container.setGridApi(api),
    [container],
  );

  return setter;
};
